import React, { useEffect, useMemo, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Send, Trash2, Paperclip } from "lucide-react";

export default function AdminMessages() {
  const [clients, setClients] = useState([]);
  const [sent, setSent] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [file, setFile] = useState(null);
  const [picked, setPicked] = useState({});
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [fileKey, setFileKey] = useState(0);

  const load = () => {
    api.get("/admin/clients").then((r) => setClients(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/admin/messages").then((r) => setSent(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return clients.filter((c) => {
      if (!q) return true;
      const name = String(c.business_name || c.name || "").toLowerCase();
      const email = String(c.owner_email || "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [clients, query]);

  const toggle = (id) => setPicked((p) => ({ ...p, [id]: !p[id] }));
  const allVisibleOn = visible.length > 0 && visible.every((c) => picked[c.id]);
  const toggleAll = () => {
    setPicked((p) => {
      const next = { ...p };
      visible.forEach((c) => { next[c.id] = !allVisibleOn; });
      return next;
    });
  };

  const send = async () => {
    const ids = Object.keys(picked).filter((id) => picked[id]);
    if (!title.trim()) return toast.error("Scrivi un titolo");
    if (!body.trim() && !file) return toast.error("Scrivi un testo oppure allega un PDF");
    if (ids.length === 0) return toast.error("Scegli almeno un locale");
    const fd = new FormData();
    fd.append("title", title.trim());
    fd.append("body", body.trim());
    fd.append("tenant_ids", JSON.stringify(ids));
    if (file) fd.append("file", file);
    setSaving(true);
    try {
      const r = await api.post("/admin/messages", fd);
      toast.success(`Inviato a ${r.data?.count || ids.length} locali`);
      setTitle("");
      setBody("");
      setFile(null);
      setFileKey((k) => k + 1);
      setPicked({});
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio non riuscito");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo messaggio? Il locale non lo vedrà più.")) return;
    try {
      await api.delete(`/admin/messages/${id}`);
      toast.success("Messaggio eliminato");
      load();
    } catch {
      toast.error("Eliminazione non riuscita");
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-4xl">
      <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-3">Comunicazioni</div>
      <h1 className="font-heading text-4xl font-bold tracking-tight mb-2 flex items-center gap-3">
        <Mail className="w-8 h-8" /> Messaggi ai locali
      </h1>
      <p className="text-velta-mute mb-10">
        Titolo, testo e, se serve, un PDF. Ogni locale vede solo il proprio messaggio, nel menu del gestionale.
        Non blocca la cassa: per chi non paga resta Sospendi.
      </p>

      <div className="bg-white border border-velta-border rounded-md p-6 mb-8">
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Titolo</label>
            <input
              data-testid="admin-msg-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={140}
              placeholder="Es. Fattura di settembre"
              className="w-full h-11 px-3 border border-velta-border rounded-md outline-none focus:border-velta-ink"
            />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Testo</label>
            <textarea
              data-testid="admin-msg-body"
              rows={5}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={4000}
              placeholder="Scrivi qui il messaggio che il locale leggerà."
              className="w-full p-3 border border-velta-border rounded-md text-sm outline-none focus:border-velta-ink resize-none"
            />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">PDF (facoltativo, max 8 MB)</label>
            <input
              key={fileKey}
              data-testid="admin-msg-file"
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm"
            />
            {file && <div className="text-xs text-velta-mute mt-1">{file.name}</div>}
          </div>
          <div>
            <div className="flex items-center justify-between gap-3 mb-2">
              <label className="text-[10px] font-mono uppercase tracking-widest text-velta-mute">Locali</label>
              <button type="button" onClick={toggleAll} className="text-xs text-[#2D5594] hover:underline">
                {allVisibleOn ? "Deseleziona visibili" : "Seleziona visibili"}
              </button>
            </div>
            <input
              data-testid="admin-msg-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca per nome o email"
              className="w-full h-10 px-3 mb-2 border border-velta-border rounded-md text-sm outline-none focus:border-velta-ink"
            />
            <div className="max-h-64 overflow-auto border border-velta-border rounded-md divide-y divide-velta-border">
              {visible.map((c) => (
                <label key={c.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-velta-bg">
                  <input
                    type="checkbox"
                    data-testid={`admin-msg-tenant-${c.id}`}
                    checked={!!picked[c.id]}
                    onChange={() => toggle(c.id)}
                  />
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{c.business_name || c.name || "Locale"}</span>
                    <span className="block truncate text-xs text-velta-mute">{c.owner_email || c.id}</span>
                  </span>
                </label>
              ))}
              {visible.length === 0 && <div className="px-3 py-6 text-sm text-velta-mute">Nessun locale</div>}
            </div>
          </div>
          <button
            data-testid="admin-msg-send"
            type="button"
            onClick={send}
            disabled={saving}
            className="inline-flex items-center gap-2 h-11 px-5 bg-velta-ink text-white rounded-md text-sm font-medium disabled:opacity-60"
          >
            <Send className="w-4 h-4" /> {saving ? "Invio…" : "Invia"}
          </button>
        </div>
      </div>

      <h2 className="font-heading text-xl font-semibold mb-4">Inviati</h2>
      <div className="space-y-3">
        {sent.map((m) => (
          <div key={m.id} className="bg-white border border-velta-border rounded-md p-4 flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <div className="font-medium">{m.title}</div>
              <div className="text-xs text-velta-mute mt-1">
                {m.business_name || m.tenant_id} · {formatDate(m.created_at)} · {m.read_at ? "letto" : "non letto"}
                {m.has_file ? ` · ${m.filename || "PDF"}` : ""}
              </div>
              {m.body && <p className="text-sm mt-2 whitespace-pre-wrap text-[#3F3F46]">{m.body}</p>}
            </div>
            <button type="button" onClick={() => del(m.id)} className="text-velta-mute hover:text-[#B73C27]" aria-label="Elimina">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        {sent.length === 0 && (
          <div className="text-sm text-velta-mute flex items-center gap-2">
            <Paperclip className="w-4 h-4" /> Nessun messaggio inviato
          </div>
        )}
      </div>
    </div>
  );
}
